// VisitorAuth 负责调 biz-api 的 /v1/visitor/auth，缓存 token，并在过期前自动刷新。
//
// 用法：
//   const auth = new VisitorAuth({ authUrl, siteKey, user });
//   const params = await auth.getConnectParams();   // 返回 { token }
//   // 失效后再调一次：返回新 token

const REFRESH_AHEAD_MS = 5 * 60 * 1000; // 过期前 5 分钟刷新

export interface VisitorAuthOptions {
  /** biz-api 的认证入口，例如 https://api.example.com/v1/visitor/auth */
  authUrl: string;
  /** 客户 SDK 公钥（公开值，可见于网页源码） */
  siteKey: string;
  /** 访客身份字段，全部可选；至少传一个 */
  user: VisitorUser;
}

export interface VisitorUser {
  externalUserId?: string;
  username?: string;
  phone?: string;
  email?: string;
  /** 设备指纹；SDK 默认在 localStorage 持久化一个 UUID 兜底 */
  deviceFp?: string;
  extra?: Record<string, unknown>;
}

export interface VisitorSettings {
  ratingEnabled: boolean;
  /** 是否向访客展示自己消息的"已读"状态；缺省视为开启 */
  readReceiptEnabled?: boolean;
  /** 是否把访客正在输入的内容实时发给坐席；缺省视为关闭 */
  typingPreviewEnabled?: boolean;
  locale?: { mode: 'auto' | 'fixed'; fixed?: string };
  welcome?: { enabled: boolean; message?: string };
  /** 浮窗标题；空 = SDK 用 opts.title 或 i18n 默认 */
  widgetTitle?: string;
  /** 租户品牌 logo 公开地址；空 = 顶部与坐席气泡都不展示 logo */
  logoUrl?: string;
}

export interface VisitorAuthResponse {
  token: string;
  expiresIn: number; // 秒
  tenantId: string;
  tenantName?: string;
  customerId: string;
  userKey: string;
  settings?: VisitorSettings;
}

export class VisitorAuth {
  private opts: VisitorAuthOptions;
  private token: string | null = null;
  private expiresAt = 0;
  private inflight: Promise<VisitorAuthResponse> | null = null;
  private metadata: VisitorAuthResponse | null = null;

  constructor(opts: VisitorAuthOptions) {
    this.opts = { ...opts, user: ensureDeviceFp(opts.user) };
  }

  /** 当前缓存的 metadata（首次刷新后才有值）。 */
  get current(): VisitorAuthResponse | null {
    return this.metadata;
  }

  /** 拼到 WS URL 上的 query 参数 */
  async getConnectParams(): Promise<Record<string, string>> {
    const r = await this.getValidToken();
    return { token: r.token };
  }

  /** 返回当前 user 的 userKey（首次刷新后才有值） */
  async getUserKey(): Promise<string> {
    const r = await this.getValidToken();
    return r.userKey;
  }

  /** 强制刷新 token */
  async refresh(): Promise<VisitorAuthResponse> {
    this.token = null;
    this.expiresAt = 0;
    return this.getValidToken();
  }

  private async getValidToken(): Promise<VisitorAuthResponse> {
    if (this.token && Date.now() < this.expiresAt - REFRESH_AHEAD_MS) {
      return this.metadata as VisitorAuthResponse;
    }
    if (this.inflight) return this.inflight;
    this.inflight = this.fetchNew()
      .then((resp) => {
        this.token = resp.token;
        this.expiresAt = Date.now() + resp.expiresIn * 1000;
        this.metadata = resp;
        return resp;
      })
      .finally(() => {
        this.inflight = null;
      });
    return this.inflight;
  }

  private async fetchNew(): Promise<VisitorAuthResponse> {
    const resp = await fetch(this.opts.authUrl, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        siteKey: this.opts.siteKey,
        user: this.opts.user,
      }),
    });
    if (!resp.ok) {
      let body: { code?: string; message?: string } | null = null;
      try {
        body = (await resp.json()) as { code?: string; message?: string };
      } catch {
        /* ignore */
      }
      const code = body?.code ?? `http_${resp.status}`;
      const msg = body?.message ?? resp.statusText;
      throw new Error(`auth failed [${code}]: ${msg}`);
    }
    return (await resp.json()) as VisitorAuthResponse;
  }
}

// 第一次访问时给 user.deviceFp 兜底一个稳定值（localStorage UUID）
const FP_KEY = 'leke.deviceFp';
function ensureDeviceFp(user: VisitorUser): VisitorUser {
  if (user.deviceFp) return user;
  try {
    let fp = localStorage.getItem(FP_KEY);
    if (!fp) {
      fp = newRandomFp();
      localStorage.setItem(FP_KEY, fp);
    }
    return { ...user, deviceFp: fp };
  } catch {
    // 隐私模式 / SSR 等情况无 localStorage：每次生成
    return { ...user, deviceFp: newRandomFp() };
  }
}

function newRandomFp(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x70; // version=7
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant=10
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
